import { useNavigation } from '@react-navigation/native';
import {
  StackHeaderProps,
  StackNavigationOptions,
} from '@react-navigation/stack';
import { theme } from 'aimo-ui';
import SkeletonLoaderRow from 'components/loading/SkeletonLoaderRow';
import PrepaidTicketAndPermitList from 'components/park/detail/PrepaidTicketAndPermitList';
import SelectCharger from 'components/park/evcharge/chargerSelection/SelectCharger';
import ChargingOptions from 'components/park/evcharge/ChargingOptions';
import ConnectCharger from 'components/park/evcharge/ConnectCharger';
import ParkingConditionsCustomEndTime from 'components/park/ParkingConditionsCustomEndTime';
import ParkingLayoutFullScreen from 'components/park/ParkingLayoutFullScreen';
import ParkingZoneDetails from 'components/park/ParkingZoneDetails';
import ParkingZoneDetailsHeadline from 'components/park/ParkingZoneDetailsHeadline';
import ParkingZoneInfo from 'components/park/ParkingZoneInfo';
import PoolingRates from 'components/park/pooling/PoolingRates';
import ResumeParking from 'components/park/ResumeParking';
import SelectTariffModalContent from 'components/park/tariff/SelectTariffModalContent';
import ParkingContext from 'context/ParkingContext';
import ParkingContextProvider from 'context/ParkingContextProvider';
import PriceEstimateContextProvider from 'context/PriceEstimateContextProvider';
import { useNavigationStateListener } from 'hooks/useNavigationStateListener';
import { StackHeaderComponentTitleSmall } from 'navigation/navigationComponents';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CompletedScreen } from 'screens/longTermParking';
import ParkScreen from 'screens/ParkScreen';
import PrepaidTicketBuyView from 'screens/prepaidTickets/PrepaidTicketBuyView.tsx';
import PrepaidTicketEdit from 'screens/prepaidTickets/PrepaidTicketEdit';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { useParkingStore } from 'stores/parkingStore';
import { ShortTermParkingScreen } from 'types/common';
import { ParkNavProps, ParkStackParamList } from 'types/navigation';
import { makePoint } from 'utils/mapUtils';
import { createStackNavigator } from 'utils/navigationHelpers';
import { getModalOptions, transitionOptions } from 'utils/navigationUtils';
import ModalStackHeader from './ModalStackHeader';

const ParkStack = createStackNavigator<ParkStackParamList>();

const withLayoutFullScreen = <P,>(
  Component: React.FC,
  currentScreen?: ShortTermParkingScreen,
  withPadding = true,
  scrollable?: boolean
): React.FC<P> => {
  return (props: P) => (
    <ParkingLayoutFullScreen
      component={Component}
      currentScreen={currentScreen}
      withPadding={withPadding}
      scrollable={scrollable}
      {...props}
    />
  );
};

const ParkingInfoWithLayout = withLayoutFullScreen(
  ParkingZoneInfo,
  'INFO',
  false
);
const ParkingConditionsCustomEndTimeWithLayout = withLayoutFullScreen(
  ParkingConditionsCustomEndTime,
  'CHOOSE_END_TIME'
);
const ResumeParkingWithLayout = withLayoutFullScreen(
  ResumeParking,
  'RESUME_PARKING'
);
const PrepaidTicketAndPermitListWithLayout = withLayoutFullScreen(
  PrepaidTicketAndPermitList,
  'PREPAID_TICKETS_AND_PERMITS'
);
const PrepaidTicketAndPermitBuyViewWithLayout = withLayoutFullScreen(
  PrepaidTicketBuyView,
  'PURCHASE_PREPAID'
);
export const PrepaidTicketEditWithLayout = withLayoutFullScreen(
  PrepaidTicketEdit,
  'EDIT_PREPAID'
);
const ConnectChargerWithLayout = withLayoutFullScreen(
  ConnectCharger,
  'CONNECT_CHARGER'
);
const SelectChargerWithLayout = withLayoutFullScreen(
  SelectCharger,
  'SELECT_CHARGER',
  false,
  false
);
const CompletedScreenWithLayout = withLayoutFullScreen(CompletedScreen);

const SelectParkingTariffWithLayout = withLayoutFullScreen(
  SelectTariffModalContent,
  'SELECT_TARIFF'
);

const PoolingRatesWithLayout = withLayoutFullScreen(PoolingRates);

const ParkZoneDetailHeader = ({
  showSeeMoreLink = true,
  showDistance = false,
}: {
  showSeeMoreLink?: boolean;
  showDistance?: boolean;
}) => {
  const { parkingZoneResult, isLoadingParkZoneDetails } =
    useContext(ParkingContext);
  const { t } = useTranslation();
  const { parkZone } = parkingZoneResult?.data || {};
  const { address } = parkZone || {};
  const { marker } = address || {};
  const markerCoordinate = marker && makePoint(marker?.coordinates);
  if (isLoadingParkZoneDetails || !parkZone) {
    return (
      <SkeletonLoaderRow accessibilityLabel={t('account.parkingDetails')} />
    );
  }
  return (
    <ParkingZoneDetailsHeadline
      parkZone={parkZone}
      markerCoordinate={markerCoordinate}
      showSeeMoreLink={showSeeMoreLink}
      showDistance={showDistance}
    />
  );
};

const createParkZoneDetailOptions = (
  showDistance = false,
  showSeeMoreLink = true
): StackNavigationOptions => ({
  header: (props: StackHeaderProps) => (
    <StackHeaderComponentTitleSmall {...props} />
  ),
  headerTitle: () => (
    <ParkZoneDetailHeader
      showDistance={showDistance}
      showSeeMoreLink={showSeeMoreLink}
    />
  ),
});

export const parkZoneDetailOptions = createParkZoneDetailOptions();

export const parkZoneDetailWithoutBackgroundOptions =
  createParkZoneDetailOptions(true, false);

const parkZoneDetailModalOptions = (onClose?: () => void) => {
  return {
    header: (props: StackHeaderProps) => (
      <ModalStackHeader
        {...props}
        showHandle
        closeButton
        closeButtonType="secondary"
        onClose={onClose}
        leftAlignHeader
      />
    ),
  };
};

const ParkNavigator = () => {
  const { t } = useTranslation();
  const { resetParkingState } = useParkingStore((state) => state);
  const modalOptions = useMemo(
    () => getModalOptions('50%', true, undefined, undefined, 400),
    []
  );
  const navigation = useNavigation<ParkNavProps>();
  // Selected once so the reference stays stable, otherwise
  // useNavigationStateListener re-subscribes on every render.
  const setActiveElement = useActiveElementStore(
    (state) => state.setActiveElement
  );

  const resetParkingFlowState = () => {
    resetParkingState();
    navigation.popTo('park');
  };

  useNavigationStateListener({
    onParkingZoneChange: setActiveElement,
  });

  return (
    <ParkingContextProvider>
      <PriceEstimateContextProvider>
        <ParkStack.Navigator>
          <ParkStack.Group
            screenOptions={{
              ...transitionOptions({ closeDuration: 800 }),
              cardOverlayEnabled: true,
              cardStyle: { backgroundColor: theme.colors.white },
              header: StackHeaderComponentTitleSmall,
            }}>
            <ParkStack.Screen
              name="park"
              component={ParkScreen}
              options={{ headerShown: false, title: t('navigation.park') }}
            />
            <ParkStack.Screen
              name="parkInfo"
              component={ParkingInfoWithLayout}
              options={parkZoneDetailWithoutBackgroundOptions}
            />
            <ParkStack.Screen
              name="parkEndTime"
              component={ParkingConditionsCustomEndTimeWithLayout}
              options={parkZoneDetailOptions}
            />
            <ParkStack.Screen
              name="parkResume"
              component={ResumeParkingWithLayout}
              options={parkZoneDetailOptions}
            />
            <ParkStack.Screen
              name="parkPrepaidTicketsAndPermitsList"
              component={PrepaidTicketAndPermitListWithLayout}
              options={{
                title: t('parking.prepaidTickets.prepaidListLabel'),
              }}
            />
            <ParkStack.Screen
              name="parkPrepaidTicketsAndPermitsBuy"
              component={PrepaidTicketAndPermitBuyViewWithLayout}
            />
            <ParkStack.Screen
              name="parkPrepaidTicketsAndPermitsEdit"
              component={PrepaidTicketEditWithLayout}
              options={{
                title: t('parking.prepaidTickets.editPrepaidTitle'),
              }}
            />
            <ParkStack.Screen
              name="chargeConnection"
              component={ConnectChargerWithLayout}
              options={parkZoneDetailOptions}
            />
            <ParkStack.Screen
              name="chargeSelect"
              component={SelectChargerWithLayout}
              options={parkZoneDetailOptions}
            />
            <ParkStack.Screen
              name="parkingTariffs"
              component={SelectParkingTariffWithLayout}
              options={{
                title: t('parking.tariff.selectTariff'),
              }}
            />
            <ParkStack.Screen
              name="poolingRates"
              component={PoolingRatesWithLayout}
              options={{
                title: t('parking.poolingGroup.poolingRates'),
              }}
            />
          </ParkStack.Group>
          <ParkStack.Group
            screenOptions={{
              ...modalOptions.screenOptions,
            }}>
            <ParkStack.Screen
              name="parkDetail"
              component={ParkingZoneDetails}
              options={{
                ...parkZoneDetailModalOptions(resetParkingFlowState),
                headerTitle: () =>
                  ParkZoneDetailHeader({ showSeeMoreLink: true }),
              }}
              listeners={{
                focus: () => {
                  useParkzoneStore.getState().setSelectedParkingZoneTab('PARK');
                },
              }}
            />
            <ParkStack.Screen
              name="chargeOptions"
              component={ChargingOptions}
              options={{
                ...parkZoneDetailModalOptions(),
                title: t('parking.chargingOptions'),
              }}
            />
          </ParkStack.Group>
        </ParkStack.Navigator>
      </PriceEstimateContextProvider>
    </ParkingContextProvider>
  );
};

export default ParkNavigator;
